import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowRightLeft, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Loader2,
  Info,
  ExternalLink
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';

interface BridgeRoute {
  sourceNetwork: string;
  destNetwork: string;
  supportedTokens: string[];
  estimatedTime: string;
  minAmount: number;
  maxAmount: number;
}

interface BridgeQuote {
  sourceNetwork: string;
  destNetwork: string;
  token: string;
  amount: number;
  fee: number;
  estimatedReceived: number;
  estimatedTime: string;
  exchangeRate: number;
}

interface BridgeTransaction {
  id: string;
  sourceNetwork: string;
  destNetwork: string;
  token: string;
  amount: number;
  fee: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  sourceTxHash?: string;
  destTxHash?: string;
  createdAt: string;
  completedAt?: string;
}

const NETWORKS = [
  { id: 'ethereum', name: 'Ethereum', icon: 'ETH' },
  { id: 'polygon', name: 'Polygon', icon: 'MATIC' },
  { id: 'bsc', name: 'BNB Chain', icon: 'BNB' },
  { id: 'solana', name: 'Solana', icon: 'SOL' },
];


export default function Bridge() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'bridge' | 'history'>('bridge');
  const [sourceNetwork, setSourceNetwork] = useState('ethereum');
  const [destNetwork, setDestNetwork] = useState('polygon');
  const [selectedToken, setSelectedToken] = useState('');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<BridgeQuote | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch available routes
  const { data: routes } = useQuery({
    queryKey: ['bridge-routes'],
    queryFn: async () => {
      const response = await api.get('/crypto/bridge/routes');
      return response.data as BridgeRoute[];
    },
  });

  // Fetch route details
  const { data: routeDetails } = useQuery({
    queryKey: ['bridge-route', sourceNetwork, destNetwork, selectedToken],
    queryFn: async () => {
      const response = await api.get(`/crypto/bridge/routes/${sourceNetwork}/${destNetwork}/${selectedToken}`);
      return response.data as BridgeRoute;
    },
    enabled: !!sourceNetwork && !!destNetwork && !!selectedToken,
  });

  // Fetch bridge history
  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ['bridge-history'],
    queryFn: async () => {
      const response = await api.get('/crypto/bridge/history');
      return response.data as BridgeTransaction[];
    },
    enabled: activeTab === 'history',
  });

  // Get quote mutation
  const quoteMutation = useMutation({
    mutationFn: async (data: { sourceNetwork: string; destNetwork: string; token: string; amount: number }) => {
      const response = await api.post('/crypto/bridge/quote', data);
      return response.data as BridgeQuote;
    },
    onSuccess: (data) => {
      setQuote(data);
    },
  });

  // Initiate bridge mutation
  const bridgeMutation = useMutation({
    mutationFn: async (data: { sourceNetwork: string; destNetwork: string; token: string; amount: number }) => {
      const response = await api.post('/crypto/bridge/initiate', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bridge-history'] });
      setShowConfirmModal(false);
      setAmount('');
      setQuote(null);
      setActiveTab('history');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleGetQuote = () => {
    if (sourceNetwork && destNetwork && selectedToken && amount) {
      quoteMutation.mutate({
        sourceNetwork,
        destNetwork,
        token: selectedToken,
        amount: parseFloat(amount),
      });
    }
  };

  const handleBridge = () => {
    if (quote) {
      bridgeMutation.mutate({
        sourceNetwork: quote.sourceNetwork,
        destNetwork: quote.destNetwork,
        token: quote.token,
        amount: quote.amount,
      });
    }
  };

  const swapNetworks = () => {
    const temp = sourceNetwork;
    setSourceNetwork(destNetwork);
    setDestNetwork(temp);
    setQuote(null);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      COMPLETED: 'success',
      PENDING: 'warning',
      PROCESSING: 'info',
      FAILED: 'error',
    };
    const labels: Record<string, string> = {
      COMPLETED: 'Concluído',
      PENDING: 'Pendente',
      PROCESSING: 'Processando',
      FAILED: 'Falhou',
    };
    return <Badge variant={variants[status] || 'info'}>{labels[status] || status}</Badge>;
  };

  const getNetworkName = (id: string) => {
    return NETWORKS.find(n => n.id === id)?.name || id;
  };

  // Get available tokens for selected route
  const availableTokens = routes?.find(
    r => r.sourceNetwork === sourceNetwork && r.destNetwork === destNetwork
  )?.supportedTokens || [];

  return (
    <PageContainer
      title="Bridge"
      description="Transfira tokens entre diferentes redes blockchain"
      icon={ArrowRightLeft}
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'bridge' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('bridge')}
        >
          <ArrowRightLeft className="w-4 h-4 mr-2" />
          Bridge
        </Button>
        <Button
          variant={activeTab === 'history' ? 'primary' : 'ghost'}
          onClick={() => setActiveTab('history')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Histórico
        </Button>
      </div>

      {/* Bridge Tab */}
      {activeTab === 'bridge' && (
        <div className="max-w-xl mx-auto space-y-6">
          <Card>
            <div className="p-6 space-y-6">
              {/* Network Selection */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-surface-400 mb-2">De</label>
                  <Select
                    value={sourceNetwork}
                    onChange={(e) => {
                      setSourceNetwork(e.target.value);
                      setSelectedToken('');
                      setQuote(null);
                    }}
                    className="w-full"
                  >
                    {NETWORKS.map(network => (
                      <option key={network.id} value={network.id} disabled={network.id === destNetwork}>
                        {network.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-6"
                  onClick={swapNetworks}
                >
                  <ArrowRightLeft className="w-5 h-5" />
                </Button>

                <div className="flex-1">
                  <label className="block text-sm text-surface-400 mb-2">Para</label>
                  <Select
                    value={destNetwork}
                    onChange={(e) => {
                      setDestNetwork(e.target.value);
                      setSelectedToken('');
                      setQuote(null);
                    }}
                    className="w-full"
                  >
                    {NETWORKS.map(network => (
                      <option key={network.id} value={network.id} disabled={network.id === sourceNetwork}>
                        {network.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Token Selection */}
              <div>
                <label className="block text-sm text-surface-400 mb-2">Token</label>
                <Select
                  value={selectedToken}
                  onChange={(e) => {
                    setSelectedToken(e.target.value);
                    setQuote(null);
                  }}
                  className="w-full"
                >
                  <option value="">Selecione um token</option>
                  {availableTokens.map(token => (
                    <option key={token} value={token}>{token}</option>
                  ))}
                </Select>
              </div>

              {/* Amount Input */}
              <div>
                <Input
                  type="number"
                  label="Quantidade"
                  placeholder="0.0000"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setQuote(null);
                  }}
                  disabled={!selectedToken}
                />
                {routeDetails && (
                  <p className="text-xs text-surface-400 mt-1">
                    Mín: {formatCurrency(routeDetails.minAmount)} | Máx: {formatCurrency(routeDetails.maxAmount)}
                  </p>
                )}
              </div>

              {/* Get Quote Button */}
              <Button
                className="w-full"
                onClick={handleGetQuote}
                disabled={!selectedToken || !amount || quoteMutation.isPending}
              >
                {quoteMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  'Obter Cotação'
                )}
              </Button>

              {/* Quote Display */}
              {quote && (
                <div className="p-4 bg-surface-800 rounded-lg space-y-3">
                  <h4 className="font-medium text-surface-100">Resumo da Transferência</h4>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Você envia</span>
                    <span className="text-surface-100">
                      {formatCurrency(quote.amount)} {quote.token}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Taxa</span>
                    <span className="text-surface-100">
                      {formatCurrency(quote.fee)} {quote.token}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Você recebe (estimado)</span>
                    <span className="text-success font-medium">
                      {formatCurrency(quote.estimatedReceived)} {quote.token}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-surface-400">Tempo estimado</span>
                    <span className="text-surface-100">{quote.estimatedTime}</span>
                  </div>

                  <Button
                    className="w-full mt-4"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    Iniciar Bridge
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Info Card */}
          <Card>
            <div className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-surface-400">
                <p className="mb-2">
                  O bridge permite transferir tokens entre diferentes redes blockchain.
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Taxas variam conforme a rede e congestionamento</li>
                  <li>Tempo de transferência pode variar de minutos a horas</li>
                  <li>Verifique sempre o endereço de destino</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Histórico de Bridges</h3>
            {loadingHistory ? (
              <div className="text-center py-8 text-surface-400">Carregando...</div>
            ) : history && history.length > 0 ? (
              <div className="space-y-4">
                {history.map((tx) => (
                  <div key={tx.id} className="p-4 bg-surface-800 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-surface-100 font-medium">{getNetworkName(tx.sourceNetwork)}</span>
                        <ArrowRight className="w-4 h-4 text-surface-400" />
                        <span className="text-surface-100 font-medium">{getNetworkName(tx.destNetwork)}</span>
                      </div>
                      {getStatusBadge(tx.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-surface-400">Quantidade</p>
                        <p className="text-surface-100">{formatCurrency(tx.amount)} {tx.token}</p>
                      </div>
                      <div>
                        <p className="text-surface-400">Taxa</p>
                        <p className="text-surface-100">{formatCurrency(tx.fee)} {tx.token}</p>
                      </div>
                      <div>
                        <p className="text-surface-400">Iniciado em</p>
                        <p className="text-surface-100">{formatDate(tx.createdAt)}</p>
                      </div>
                      {tx.completedAt && (
                        <div>
                          <p className="text-surface-400">Concluído em</p>
                          <p className="text-surface-100">{formatDate(tx.completedAt)}</p>
                        </div>
                      )}
                    </div>

                    {(tx.sourceTxHash || tx.destTxHash) && (
                      <div className="flex gap-4 mt-3 pt-3 border-t border-surface-700">
                        {tx.sourceTxHash && (
                          <a
                            href={`#`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-400 hover:underline flex items-center gap-1"
                          >
                            TX Origem <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {tx.destTxHash && (
                          <a
                            href={`#`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-400 hover:underline flex items-center gap-1"
                          >
                            TX Destino <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-surface-400">
                Nenhuma transferência encontrada
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Bridge"
      >
        {quote && (
          <div className="space-y-4">
            <div className="p-4 bg-surface-800 rounded-lg space-y-3">
              <div className="flex items-center justify-center gap-4 text-lg">
                <span className="text-surface-100">{getNetworkName(quote.sourceNetwork)}</span>
                <ArrowRight className="w-5 h-5 text-primary-400" />
                <span className="text-surface-100">{getNetworkName(quote.destNetwork)}</span>
              </div>
              
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-400">
                  {formatCurrency(quote.amount)} {quote.token}
                </p>
                <p className="text-sm text-surface-400">
                  Você receberá ~{formatCurrency(quote.estimatedReceived)} {quote.token}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-warning/10 rounded-lg">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <p className="text-sm text-warning">
                Esta operação não pode ser desfeita. Verifique os detalhes antes de confirmar.
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBridge}
                disabled={bridgeMutation.isPending}
                className="flex-1"
              >
                {bridgeMutation.isPending ? 'Processando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
